import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../api/client'
import { useAuthStore, type User } from '../store/authStore'
import { edgeConnStatus, EDGE_STATUS_LABEL, EDGE_STATUS_DOT, EDGE_STATUS_TEXT } from '../utils/edgeStatus'

// ── API calls ─────────────────────────────────────────────────────────────────

type ModbusMode = 'slave' | 'master'

type MasterConnErrorCategory = 'unreachable' | 'refused' | 'reset' | 'unknown'

// 'deploy' is I-SOFT-operator-only (operator.py) — tenants can only ever
// PATCH between 'base'/'extended', but a node an operator has switched to
// 'deploy' still shows up here via GET, so the type includes it for display.
type ConfigTier = 'base' | 'extended' | 'deploy'

interface EdgeNode {
  id: string
  name: string
  modbus_mode: ModbusMode
  config_tier: ConfigTier
  modbus_target_host: string | null
  modbus_target_port: number
  modbus_listen_port: number
  modbus_timeout_seconds: number
  modbus_reconnect_delay_seconds: number
  ssh_admin_ip: string | null
  ingress_key: string
  active: boolean
  created_at: string
  last_seen_at: string | null
  last_seen_ip: string | null
  master_conn_error: MasterConnErrorCategory | null
  master_conn_error_detail: string | null
  master_conn_error_at: string | null
}

// null means healthy — the address itself doesn't go stale like a device
// reading does, so swapping it for the error category loses nothing.
const MASTER_CONN_ERROR_LABEL: Record<MasterConnErrorCategory, string> = {
  unreachable: 'Nedostupno',
  refused:     'Odbijeno',
  reset:       'Prekinuto',
  unknown:     'Nepoznata greška',
}

const CONFIG_TIER_LABEL: Record<ConfigTier, string> = {
  base:     'Osnovni',
  extended: 'Prošireni',
  deploy:   'Deploy',
}

interface EdgeNodeCreateBody {
  name: string
  modbus_mode?: ModbusMode
  modbus_target_host?: string
  modbus_target_port?: number
  modbus_listen_port?: number
  modbus_timeout_seconds?: number
  modbus_reconnect_delay_seconds?: number
  ssh_admin_ip?: string | null
}

function apiError(e: unknown, fallback: string): string {
  return (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? fallback
}

// B3: POST /setup/import/meters returns this (202) instead of an
// ImportResultData (200) once the file's row count clears the backend's
// own threshold (1000, re-checked server-side — the client-side count
// CsvImportCard does before submitting is a courtesy warning, not the
// real gate).
interface ImportJobPending {
  job_id: string
  status: 'pending'
  row_count: number
}

type ImportJobStatus = 'pending' | 'processing' | 'done' | 'failed'

interface ImportJobSummary {
  id: string
  status: ImportJobStatus
  row_count: number
  created_at: string
  updated_at: string
  result: ImportResultData | null
  error_message: string | null
  has_errors_csv: boolean
}

function EdgeStatusBadge({ node }: { node: EdgeNode }) {
  const status = edgeConnStatus(node)
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium">
      <span className={`w-2 h-2 rounded-full ${EDGE_STATUS_DOT[status]}`} />
      <span className={EDGE_STATUS_TEXT[status]}>{EDGE_STATUS_LABEL[status]}</span>
    </span>
  )
}

const setupApi = {
  importUsers:  (file: File) => { const fd = new FormData(); fd.append('file', file); return api.post('/setup/import/users', fd) },
  importMeters: (file: File) => {
    const fd = new FormData(); fd.append('file', file)
    return api.post<ImportResultData | ImportJobPending>('/setup/import/meters', fd)
  },
  getImportJob: (jobId: string) => api.get<ImportJobSummary>(`/setup/import/jobs/${jobId}`),
  importScada:  (file: File) => { const fd = new FormData(); fd.append('file', file); return api.post('/setup/import/scada', fd) },
  importZones:  (file: File) => { const fd = new FormData(); fd.append('file', file); return api.post('/setup/import/zones', fd) },
  importZoneLinks: (file: File) => { const fd = new FormData(); fd.append('file', file); return api.post('/setup/import/zone-links', fd) },
  importPumpReservoirLinks: (file: File) => { const fd = new FormData(); fd.append('file', file); return api.post('/setup/import/pump-reservoir-links', fd) },
  listEdgeNodes:  () => api.get<EdgeNode[]>('/setup/edge-nodes'),
  createEdgeNode: (body: EdgeNodeCreateBody) => api.post<EdgeNode>('/setup/edge-nodes', body),
  regenerateKey:  (id: string) => api.post<EdgeNode>(`/setup/edge-nodes/${id}/regenerate-key`),
  createDownloadToken: (id: string) => api.post<{ token: string; expires_at: number }>(`/setup/edge-nodes/${id}/download-token`),
  updateEdgeNode: (id: string, body: object) => api.patch<EdgeNode>(`/setup/edge-nodes/${id}`, body),
  deleteEdgeNode: (id: string) => api.delete(`/setup/edge-nodes/${id}`),
}

// ── Modbus mode picker (shared by create form + per-node edit) ─────────────────

interface ModbusDraft {
  modbus_mode: ModbusMode
  modbus_target_host: string
  modbus_target_port: string
  modbus_listen_port: string
  modbus_timeout_seconds: string
  modbus_reconnect_delay_seconds: string
  ssh_admin_ip: string
}

function emptyModbusDraft(): ModbusDraft {
  return { modbus_mode: 'slave', modbus_target_host: '', modbus_target_port: '', modbus_listen_port: '', modbus_timeout_seconds: '', modbus_reconnect_delay_seconds: '', ssh_admin_ip: '' }
}

function modbusDraftFromNode(n: EdgeNode): ModbusDraft {
  return {
    modbus_mode: n.modbus_mode,
    modbus_target_host: n.modbus_target_host ?? '',
    modbus_target_port: String(n.modbus_target_port),
    modbus_listen_port: String(n.modbus_listen_port),
    modbus_timeout_seconds: String(n.modbus_timeout_seconds),
    modbus_reconnect_delay_seconds: String(n.modbus_reconnect_delay_seconds),
    ssh_admin_ip: n.ssh_admin_ip ?? '',
  }
}

function modbusBody(d: ModbusDraft): Omit<EdgeNodeCreateBody, 'name'> {
  const modbus_listen_port = d.modbus_listen_port ? parseInt(d.modbus_listen_port, 10) : 502
  const ssh_admin_ip = d.ssh_admin_ip.trim() || null
  if (d.modbus_mode === 'slave') return { modbus_mode: 'slave', modbus_listen_port, ssh_admin_ip }
  return {
    modbus_mode: 'master',
    modbus_target_host: d.modbus_target_host.trim(),
    modbus_target_port: d.modbus_target_port ? parseInt(d.modbus_target_port, 10) : 502,
    modbus_listen_port,
    modbus_timeout_seconds: d.modbus_timeout_seconds ? parseInt(d.modbus_timeout_seconds, 10) : 5,
    modbus_reconnect_delay_seconds: d.modbus_reconnect_delay_seconds ? parseInt(d.modbus_reconnect_delay_seconds, 10) : 10,
    ssh_admin_ip,
  }
}

const MODBUS_INPUT = 'px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500'

function ModbusModeFields({ draft, onChange }: { draft: ModbusDraft; onChange: (d: ModbusDraft) => void }) {
  return (
    <div>
      <div className="flex gap-2">
        {(['slave', 'master'] as const).map((m) => (
          <button key={m} type="button" onClick={() => onChange({ ...draft, modbus_mode: m })}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
              draft.modbus_mode === m ? 'bg-brand-cyan text-brand-midnight border-brand-cyan' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}>
            {m === 'slave' ? 'Slave' : 'Master'}
          </button>
        ))}
      </div>
      <div className="mt-2">
        <input type="number" value={draft.modbus_listen_port}
          onChange={(e) => onChange({ ...draft, modbus_listen_port: e.target.value })}
          placeholder="Port (502)" className={`w-24 ${MODBUS_INPUT}`} />
        <p className="mt-1 text-[11px] text-gray-500">
          Port na koji ovaj čvor sluša Modbus TCP — menjajte samo ako je 502 već zauzet na ciljnom hostu.
        </p>
      </div>
      <div className="mt-2">
        <input type="text" value={draft.ssh_admin_ip}
          onChange={(e) => onChange({ ...draft, ssh_admin_ip: e.target.value })}
          placeholder="SSH pristup, npr. 203.0.113.5 ili 203.0.113.0/24" className={`w-full ${MODBUS_INPUT}`} />
        <p className="mt-1 text-[11px] text-gray-500">
          SSH pristup (IP ili opseg) — opciono, ostavite prazno ako ne želite SSH pristup na čvor. Važi tek
          od sledećeg preuzimanja i pokretanja setup.sh na čvoru, ne primenjuje se odmah na već pokrenut čvor.
        </p>
      </div>
      {draft.modbus_mode === 'master' && (
        <div className="flex flex-wrap gap-2 mt-2">
          <input type="text" value={draft.modbus_target_host}
            onChange={(e) => onChange({ ...draft, modbus_target_host: e.target.value })}
            placeholder="IP adresa PLC-a *" className={`flex-1 min-w-[160px] ${MODBUS_INPUT}`} />
          <input type="number" value={draft.modbus_target_port}
            onChange={(e) => onChange({ ...draft, modbus_target_port: e.target.value })}
            placeholder="Port (502)" className={`w-24 ${MODBUS_INPUT}`} />
          <input type="number" value={draft.modbus_timeout_seconds}
            onChange={(e) => onChange({ ...draft, modbus_timeout_seconds: e.target.value })}
            placeholder="Timeout s (5)" className={`w-28 ${MODBUS_INPUT}`} />
          <input type="number" value={draft.modbus_reconnect_delay_seconds}
            onChange={(e) => onChange({ ...draft, modbus_reconnect_delay_seconds: e.target.value })}
            placeholder="Reconnect s (10)" className={`w-32 ${MODBUS_INPUT}`} />
        </div>
      )}
    </div>
  )
}

// ── Section wrapper ───────────────────────────────────────────────────────────

function Section({ title, children, wide = false }: { title: string; children: React.ReactNode; wide?: boolean }) {
  return (
    <div className={`bg-white rounded-xl border border-gray-200 shadow-xs overflow-hidden flex-1 ${wide ? 'min-w-[360px]' : 'min-w-[320px]'}`}>
      <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{title}</h2>
      </div>
      <div className="p-6">{children}</div>
    </div>
  )
}

// ── CSV import card ───────────────────────────────────────────────────────────

type ImportResultData = {
  imported: number
  updated?: number
  deactivated?: number
  superseded?: number
  skipped: number
  errors: { row: number; message: string }[]
  accounts?: { email: string; activation_token: string }[]
}

// Shared between the synchronous result panel and a completed async job's
// result panel (ImportJobPanel below) — same counts, same shape either way.
function ImportResultSummary({ result, showUpdated, showAccounts }: {
  result: ImportResultData
  showUpdated: boolean
  showAccounts: boolean
}) {
  return (
    <div className="space-y-1.5">
      <p className="text-xs">
        <span className="text-emerald-600 font-semibold">{result.imported} {showUpdated ? 'dodato' : 'uvezeno'}</span>
        {showUpdated && (result.updated ?? 0) > 0 && (
          <span className="text-brand-cyan-text font-semibold ml-2">{result.updated} ažurirano</span>
        )}
        {showUpdated && (result.deactivated ?? 0) > 0 && (
          <span className="text-gray-500 font-semibold ml-2">{result.deactivated} obrisano</span>
        )}
        {showUpdated && (result.superseded ?? 0) > 0 && (
          <span className="text-gray-400 ml-2">{result.superseded} zamenjeno</span>
        )}
        {result.skipped > 0 && <span className="text-gray-400 ml-2">{result.skipped} preskočeno</span>}
      </p>
      {result.errors.length > 0 && (
        <ul className="space-y-0.5 max-h-28 overflow-y-auto">
          {result.errors.map((e, i) => (
            <li key={i} className="text-xs text-red-500">Red {e.row}: {e.message}</li>
          ))}
        </ul>
      )}
      {showAccounts && result.accounts && result.accounts.length > 0 && (
        <div className="mt-2 space-y-1 max-h-36 overflow-y-auto">
          <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Aktivacioni linkovi</p>
          {result.accounts.map((a) => (
            <div key={a.email} className="flex items-center gap-2 text-xs bg-gray-50 rounded-sm px-2 py-1">
              <span className="text-gray-700 w-40 truncate shrink-0">{a.email}</span>
              <button
                onClick={() => navigator.clipboard.writeText(`${window.location.origin}/activate?token=${a.activation_token}`)}
                className="text-brand-cyan-text hover:text-brand-midnight underline underline-offset-2">
                Kopiraj link
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// Polls a pending/processing B3 job until it leaves that state. No rush —
// actual processing only happens every ~10 minutes via the server timer —
// so a relaxed interval is enough; completion also fires a push + email
// server-side, this polling isn't the only way the user finds out.
function ImportJobPanel({ jobId, showUpdated, showAccounts }: {
  jobId: string
  showUpdated: boolean
  showAccounts: boolean
}) {
  const { token } = useAuthStore()
  const { data: job } = useQuery({
    queryKey: ['import-job', jobId],
    queryFn: () => setupApi.getImportJob(jobId).then((r) => r.data),
    refetchInterval: (query) => {
      const status = query.state.data?.status
      return status === 'done' || status === 'failed' ? false : 15_000
    },
  })

  const downloadErrors = async () => {
    try {
      const res = await fetch(`/api/v1/setup/import/jobs/${jobId}/errors.csv`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      if (!res.ok) return
      const blob = await res.blob()
      const objUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = objUrl
      a.download = `uvoz-greske-${jobId.slice(0, 8)}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(objUrl)
    } catch {
      // Best-effort secondary action — no dedicated error surface for it.
    }
  }

  if (!job || job.status === 'pending' || job.status === 'processing') {
    return (
      <div className="border border-blue-200 bg-blue-50 rounded-lg p-3 text-xs text-brand-cyan-text">
        Uvoz je zakazan. Obrada se odvija periodično na serveru (obično tokom noći) — dobićete obaveštenje kada bude završeno.
      </div>
    )
  }

  if (job.status === 'failed') {
    return <p className="text-xs text-red-500">Uvoz nije uspeo: {job.error_message ?? 'nepoznata greška'}</p>
  }

  return (
    <div className="space-y-1.5">
      {job.result && <ImportResultSummary result={job.result} showUpdated={showUpdated} showAccounts={showAccounts} />}
      {job.has_errors_csv && (
        <button onClick={downloadErrors} className="text-xs text-brand-cyan-text hover:text-brand-midnight underline underline-offset-2">
          Preuzmi CSV sa greškama
        </button>
      )}
    </div>
  )
}

// Download-only, no matching import endpoint -- the combined workbook's
// value is filling in zones+devices+pipelines together with live
// cross-sheet dropdowns in one file; recipients still save each tab as
// its own CSV and upload through the per-entity CsvImportCards above,
// unchanged. Same fetch-with-auth-then-blob pattern those cards' own
// template download already uses.
function TemplateDownloadCard({ title, description, templateUrl, templateFilename }: {
  title: string
  description: string
  templateUrl: string
  templateFilename: string
}) {
  const { token } = useAuthStore()
  const [err, setErr] = useState('')

  const download = async () => {
    setErr('')
    try {
      const res = await fetch(templateUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        setErr((body as { detail?: string }).detail ?? 'Greška pri preuzimanju šablona')
        return
      }
      const blob = await res.blob()
      const objUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = objUrl
      a.download = templateFilename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(objUrl)
    } catch {
      setErr('Greška pri preuzimanju šablona')
    }
  }

  return (
    <div className="border border-gray-200 rounded-xl p-4 space-y-2">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-gray-800">{title}</p>
          <p className="text-xs text-gray-500 mt-0.5">{description}</p>
        </div>
        <button onClick={download}
          className="shrink-0 text-sm font-medium bg-brand-cyan hover:bg-brand-cyan-dark text-brand-midnight px-4 py-2 rounded-lg transition-colors">
          Preuzmi
        </button>
      </div>
      {err && <p className="text-xs text-red-500">{err}</p>}
    </div>
  )
}

function CsvImportCard({
  title, description, templateUrl, templateFilename, onImport, showAccounts = false, showUpdated = false, asyncThreshold,
}: {
  title: string
  description: string
  templateUrl: string
  templateFilename: string
  onImport: (file: File) => Promise<{ data: ImportResultData | ImportJobPending }>
  showAccounts?: boolean
  showUpdated?: boolean
  // B3: meters-only for this first pass. At/above this row count, the file
  // gets an inline confirm before upload and the response becomes an
  // ImportJobPending to poll rather than a synchronous ImportResultData.
  asyncThreshold?: number
}) {
  const { token } = useAuthStore()
  const [file, setFile]         = useState<File | null>(null)
  const [loading, setLoading]   = useState(false)
  const [result, setResult]     = useState<ImportResultData | null>(null)
  const [jobId, setJobId]       = useState<string | null>(null)
  const [err, setErr]           = useState('')
  const [templateErr, setTemplateErr] = useState('')
  // Courtesy warning only — the backend independently re-checks row count
  // and is the real decision-maker, so this doesn't need to be exact.
  const [pendingRowCount, setPendingRowCount] = useState<number | null>(null)
  const [confirmedLarge, setConfirmedLarge]   = useState(false)
  const needsConfirm = pendingRowCount !== null && !confirmedLarge

  const onFileChange = async (f: File | null) => {
    setResult(null); setJobId(null); setErr(''); setPendingRowCount(null); setConfirmedLarge(false)
    setFile(f)
    if (f && asyncThreshold) {
      const text = await f.text()
      const rowCount = text.split('\n').filter((l) => l.trim().length > 0).length - 1
      if (rowCount >= asyncThreshold) setPendingRowCount(rowCount)
    }
  }

  const handle = async () => {
    if (!file || needsConfirm) return
    setLoading(true); setErr(''); setResult(null); setJobId(null)
    try {
      const res = await onImport(file)
      if ('job_id' in res.data) {
        setJobId(res.data.job_id)
      } else {
        setResult(res.data)
      }
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setErr(detail ?? 'Greška pri uvozu')
    } finally {
      setLoading(false)
    }
  }

  const downloadTemplate = async () => {
    setTemplateErr('')
    try {
      const res = await fetch(templateUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        setTemplateErr((body as { detail?: string }).detail ?? 'Greška pri preuzimanju šablona')
        return
      }
      const blob = await res.blob()
      const objUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = objUrl
      a.download = templateFilename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(objUrl)
    } catch {
      setTemplateErr('Greška pri preuzimanju šablona')
    }
  }

  return (
    <div className="border border-gray-200 rounded-xl p-4 space-y-3">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-gray-800">{title}</p>
          <p className="text-xs text-gray-500 mt-0.5">{description}</p>
        </div>
        <button onClick={downloadTemplate}
          className="shrink-0 text-xs text-brand-cyan-text hover:text-brand-midnight underline underline-offset-2">
          Šablon
        </button>
      </div>
      {templateErr && <p className="text-xs text-red-500">{templateErr}</p>}
      <div className="flex items-center gap-2">
        <label className="flex-1 flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 cursor-pointer hover:border-gray-400 transition-colors">
          <span className="text-xs text-gray-500 truncate">{file ? file.name : 'Odaberite CSV fajl…'}</span>
          <input type="file" accept=".csv" className="sr-only"
            onChange={(e) => { void onFileChange(e.target.files?.[0] ?? null) }} />
        </label>
        <button onClick={handle} disabled={!file || loading || needsConfirm}
          className="shrink-0 text-sm font-medium bg-brand-cyan hover:bg-brand-cyan-dark disabled:opacity-50 text-brand-midnight px-4 py-2 rounded-lg transition-colors">
          {loading ? 'Uvoz…' : 'Uvezi'}
        </button>
      </div>
      {needsConfirm && (
        <div className="border border-amber-200 bg-amber-50 rounded-xl p-3 space-y-2">
          <p className="text-xs text-amber-800">
            Fajl sadrži {pendingRowCount} redova. Uvoz će biti zakazan i obrađen prema raspoloživosti servera
            (obično tokom noći), a dobićete obaveštenje kada bude završen.
          </p>
          <div className="flex gap-2">
            <button onClick={() => setConfirmedLarge(true)}
              className="text-xs font-semibold bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded-lg">
              Nastavi
            </button>
            <button onClick={() => { setFile(null); setPendingRowCount(null) }}
              className="text-xs font-semibold bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-3 py-1.5 rounded-lg">
              Otkaži
            </button>
          </div>
        </div>
      )}
      {err && <p className="text-xs text-red-500">{err}</p>}
      {jobId && <ImportJobPanel jobId={jobId} showUpdated={showUpdated} showAccounts={showAccounts} />}
      {result && <ImportResultSummary result={result} showUpdated={showUpdated} showAccounts={showAccounts} />}
    </div>
  )
}

// ── CSV import section ────────────────────────────────────────────────────────

export function hasModule(user: User | null, required: string[]): boolean {
  if (!user) return false
  if (user.roles.includes('admin')) return true
  if ((user.modules?.length ?? 0) === 0) return true
  return required.some((m) => user.modules.includes(m))
}

function ImportSection() {
  const { user } = useAuthStore()
  const showMeters = hasModule(user, ['meter_reading'])
  const showScada = hasModule(user, ['scada'])
  return (
    <Section title="Uvoz podataka">
      <p className="text-sm text-gray-500 mb-4">
        Inicijalni unos podataka putem CSV fajlova. Duplikati se automatski preskačaju.
      </p>
      <div className="space-y-3">
        <CsvImportCard
          title="Radnici"
          description="Kolone: email, full_name, phone, roles (meter_reader, scada_operator…)"
          templateUrl="/api/v1/setup/import/users/template"
          templateFilename="users-template.csv"
          onImport={(f) => setupApi.importUsers(f)}
          showAccounts
        />
        {showMeters && (
          <CsvImportCard
            title="Vodomeri"
            description="Kolone: meter_serial, subscriber_name, address, meter_type, latitude, longitude"
            templateUrl="/api/v1/setup/import/meters/template"
            templateFilename="vodomeri-sablon.xlsx"
            onImport={(f) => setupApi.importMeters(f)}
            showUpdated
            asyncThreshold={1000}
          />
        )}
        {showScada && (
          <>
            <CsvImportCard
              title="SCADA uređaji"
              description="Kolone: device_key, node_type (pump/reservoir/flow_meter/pressure_sensor/level_sensor), label, location_lat, location_lng"
              templateUrl="/api/v1/setup/import/scada/template"
              templateFilename="scada-uredjaji-sablon.xlsx"
              onImport={(f) => setupApi.importScada(f)}
              showUpdated
            />
            <TemplateDownloadCard
              title="Kombinovani šablon (zone + uređaji + cevovodi)"
              description="Jedan Excel fajl, tri lista — za popunjavanje zajedno, sa padajućim listama koje se ažuriraju uživo između listova. Uvoz i dalje ide po tabeli, preko kartica ispod."
              templateUrl="/api/v1/setup/import/infrastructure/template"
              templateFilename="infrastruktura-sablon.xlsx"
            />
            <p className="text-xs text-gray-400 pt-1">
              Redosled uvoza ispod: zone → cevovodi / nadzor plovka (obe referenciraju zone i uređaje po nazivu, ne po ID-u).
            </p>
            <CsvImportCard
              title="Zone"
              description="SCADA zona (bunari, rezervoari, distribucija, izvori). Kolone: label, zone_category, description"
              templateUrl="/api/v1/setup/import/zones/template"
              templateFilename="scada-zone-sablon.xlsx"
              onImport={(f) => setupApi.importZones(f)}
            />
            <CsvImportCard
              title="Cevovodi (zona-veze)"
              description="Poveži zone u cevovodni sistem — koje snabdeva koje. Uvezite zone pre ovoga."
              templateUrl="/api/v1/setup/import/zone-links/template"
              templateFilename="zone-links-template.xlsx"
              onImport={(f) => setupApi.importZoneLinks(f)}
            />
            <CsvImportCard
              title="Nadzor plovka (pumpa–rezervoar)"
              description="Poveži pumpu sa rezervoarom čiji plovak nadgleda, radi alarma — bez daljinske kontrole pumpe. Uvezite SCADA uređaje pre ovoga."
              templateUrl="/api/v1/setup/import/pump-reservoir-links/template"
              templateFilename="pump-reservoir-links-template.xlsx"
              onImport={(f) => setupApi.importPumpReservoirLinks(f)}
            />
          </>
        )}
      </div>
    </Section>
  )
}

// ── Edge nodes section ────────────────────────────────────────────────────────

function EdgeNodesSection() {
  const qc = useQueryClient()
  const { token } = useAuthStore()
  const [newName, setNewName]             = useState('')
  const [createDraft, setCreateDraft]     = useState<ModbusDraft>(emptyModbusDraft())
  const [createErr, setCreateErr]         = useState('')
  const [revealId, setRevealId]           = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)
  const [confirmRegen, setConfirmRegen]   = useState<string | null>(null)
  const [editId, setEditId]               = useState<string | null>(null)
  const [editDraft, setEditDraft]         = useState<ModbusDraft>(emptyModbusDraft())
  const [editErr, setEditErr]             = useState('')
  const [packageErr, setPackageErr]       = useState('')
  const [downloading, setDownloading]     = useState<string | null>(null)
  const [copiedId, setCopiedId]           = useState<string | null>(null)

  const { data: nodes = [], isLoading } = useQuery({
    queryKey: ['setup-edge-nodes'],
    queryFn: () => setupApi.listEdgeNodes().then((r) => r.data),
    refetchInterval: 30_000, // keeps the connectivity status dot honest without a manual reload
  })

  const createMut = useMutation({
    mutationFn: () => setupApi.createEdgeNode({ name: newName.trim(), ...modbusBody(createDraft) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['setup-edge-nodes'] })
      setNewName(''); setCreateDraft(emptyModbusDraft()); setCreateErr('')
    },
    onError: (e: unknown) => setCreateErr(apiError(e, 'Greška pri dodavanju čvora')),
  })

  const regenMut = useMutation({
    mutationFn: (id: string) => setupApi.regenerateKey(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['setup-edge-nodes'] }); setConfirmRegen(null) },
  })

  const toggleMut = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      setupApi.updateEdgeNode(id, { active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['setup-edge-nodes'] }),
  })

  const [tierErr, setTierErr] = useState('')
  const updateTierMut = useMutation({
    mutationFn: ({ id, config_tier }: { id: string; config_tier: 'base' | 'extended' }) =>
      setupApi.updateEdgeNode(id, { config_tier }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['setup-edge-nodes'] }); setTierErr('') },
    onError: (e: unknown) => setTierErr(apiError(e, 'Greška pri promeni režima')),
  })

  const updateModeMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Omit<EdgeNodeCreateBody, 'name'> }) => setupApi.updateEdgeNode(id, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['setup-edge-nodes'] }); setEditId(null); setEditErr('') },
    onError: (e: unknown) => setEditErr(apiError(e, 'Greška pri čuvanju izmene')),
  })

  const deleteMut = useMutation({
    mutationFn: (id: string) => setupApi.deleteEdgeNode(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['setup-edge-nodes'] }); setConfirmDelete(null) },
  })

  const handleDownloadPackage = async (node: EdgeNode) => {
    setPackageErr('')
    setDownloading(node.id)
    try {
      const res = await fetch(`/api/v1/setup/edge-nodes/${node.id}/package`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        setPackageErr((body as { detail?: string }).detail ?? 'Greška pri preuzimanju paketa')
        return
      }
      const blob = await res.blob()
      const objUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = objUrl
      a.download = `isoft-edge-${node.name.toLowerCase().replace(/\s+/g, '-')}.zip`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(objUrl)
    } catch {
      setPackageErr('Greška pri preuzimanju paketa')
    } finally {
      setDownloading(null)
    }
  }

  // For a headless edge box with no browser session — mints a short-lived
  // (15 min) token-scoped URL so `curl` can pull the package with no
  // Authorization header at all, then copies the ready-to-run command.
  const copyCurlMut = useMutation({
    mutationFn: (node: EdgeNode) => setupApi.createDownloadToken(node.id).then((r) => ({ node, ...r.data })),
    onSuccess: ({ node, token }) => {
      const url = `${window.location.origin}/api/v1/setup/edge-nodes/${node.id}/package?token=${token}`
      const filename = `isoft-edge-${node.name.toLowerCase().replace(/\s+/g, '-')}.zip`
      navigator.clipboard.writeText(`curl -o ${filename} "${url}"`)
      setCopiedId(node.id)
      setTimeout(() => setCopiedId((id) => id === node.id ? null : id), 2500)
    },
    onError: (e: unknown) => setPackageErr(apiError(e, 'Greška pri generisanju curl komande')),
  })

  return (
    <Section title="Edge čvorovi" wide>
      <p className="text-sm text-gray-500 mb-4">
        Modbus edge čvorovi koja šalju podatke sa terena. Svako čvor dobija jedinstven ključ za autentifikaciju.
      </p>

      <div className="mb-5 space-y-2">
        <div className="flex gap-2">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newName.trim() && !(createDraft.modbus_mode === 'master' && !createDraft.modbus_target_host.trim())) createMut.mutate()
            }}
            placeholder="Naziv čvorovi (npr. SCADA Stanica 1)"
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={() => createMut.mutate()}
            disabled={!newName.trim() || createMut.isPending || (createDraft.modbus_mode === 'master' && !createDraft.modbus_target_host.trim())}
            className="px-4 py-2 text-sm font-medium bg-brand-cyan hover:bg-brand-cyan-dark disabled:opacity-50 text-brand-midnight rounded-lg transition-colors"
          >
            {createMut.isPending ? 'Dodavanje…' : 'Dodaj čvor'}
          </button>
        </div>
        <ModbusModeFields draft={createDraft} onChange={setCreateDraft} />
        {createErr && <p className="text-xs text-red-500">{createErr}</p>}
      </div>

      {packageErr && <p className="mb-3 text-xs text-red-500">{packageErr}</p>}

      {isLoading ? (
        <p className="text-sm text-gray-400">Učitavanje…</p>
      ) : nodes.length === 0 ? (
        <p className="text-sm text-gray-400 italic">Nema registrovanih edge čvorovi.</p>
      ) : (
        <div className="divide-y divide-gray-100 border border-gray-200 rounded-xl overflow-hidden">
          {nodes.map((node) => (
            <div key={node.id} className="p-4 bg-white">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-800 truncate">{node.name}</span>
                    <EdgeStatusBadge node={node} />
                    <span
                      title={node.master_conn_error ? (node.master_conn_error_detail ?? undefined) : undefined}
                      className={`text-xs px-2 py-0.5 rounded-full font-medium border ${
                        node.master_conn_error
                          ? 'bg-red-50 text-brand-critical-text border-red-200'
                          : 'bg-blue-50 text-brand-cyan-text border-blue-200'
                      }`}
                    >
                      {node.modbus_mode !== 'master'
                        ? 'Slave'
                        : node.master_conn_error
                        ? MASTER_CONN_ERROR_LABEL[node.master_conn_error]
                        : `Master → ${node.modbus_target_host}:${node.modbus_target_port}`}
                    </span>
                  </div>

                  <div className="mt-1.5 flex items-center gap-2">
                    <code className="text-xs text-gray-500 font-mono">
                      {revealId === node.id
                        ? node.ingress_key
                        : `${node.ingress_key.slice(0, 4)}${'•'.repeat(20)}${node.ingress_key.slice(-4)}`}
                    </code>
                    <button
                      onClick={() => setRevealId(revealId === node.id ? null : node.id)}
                      className="text-xs text-brand-cyan-text hover:text-brand-midnight"
                    >
                      {revealId === node.id ? 'Sakrij' : 'Prikaži'}
                    </button>
                  </div>

                  {node.last_seen_at && (
                    <p className="text-xs text-gray-400 mt-1">
                      Poslednji kontakt: {new Date(node.last_seen_at).toLocaleString('sr-Latn-RS')}
                      {node.last_seen_ip && ` · ${node.last_seen_ip}`}
                    </p>
                  )}

                  <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                    <span className="text-xs text-gray-500">Režim:</span>
                    {(['base', 'extended'] as const).map((t) => (
                      <button key={t} type="button"
                        onClick={() => updateTierMut.mutate({ id: node.id, config_tier: t })}
                        disabled={updateTierMut.isPending && updateTierMut.variables?.id === node.id}
                        title={t === 'extended'
                          ? 'Prošireni režim — spisak uređaja/registara se učitava sa servera i osvežava periodično, bez ponovnog preuzimanja paketa'
                          : 'Osnovni režim — konfiguracija fiksirana u paketu prilikom preuzimanja'}
                        className={`text-xs px-2.5 py-1 rounded-full font-medium border transition-colors disabled:opacity-50 ${
                          node.config_tier === t
                            ? 'bg-brand-cyan text-brand-midnight border-brand-cyan'
                            : 'border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}>
                        {CONFIG_TIER_LABEL[t]}
                      </button>
                    ))}
                    {node.config_tier === 'deploy' && (
                      <span
                        title="Deploy režim — postavlja isključivo I-SOFT osoblje, koristi se za internu dijagnostiku"
                        className="text-xs px-2.5 py-1 rounded-full font-medium bg-purple-50 text-purple-700 border border-purple-200">
                        Deploy (I-SOFT)
                      </span>
                    )}
                  </div>
                  {tierErr && updateTierMut.variables?.id === node.id && (
                    <p className="text-xs text-red-500 mt-1">{tierErr}</p>
                  )}
                  <p className="mt-1 text-[11px] text-gray-400">
                    Promena režima važi tek nakon ponovnog preuzimanja paketa i pokretanja setup.sh na čvoru.
                  </p>
                </div>

                <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
                  <button onClick={() => handleDownloadPackage(node)} disabled={downloading === node.id}
                    className="text-xs px-2.5 py-1.5 rounded-lg border border-blue-200 text-brand-cyan-text hover:bg-blue-50 disabled:opacity-50 transition-colors">
                    {downloading === node.id ? 'Preuzimanje…' : 'Preuzmi'}
                  </button>
                  <button onClick={() => copyCurlMut.mutate(node)}
                    disabled={copyCurlMut.isPending && copyCurlMut.variables?.id === node.id}
                    title="Za čvor bez pristupa pregledaču — kopira curl komandu koja važi 15 minuta"
                    className="text-xs px-2.5 py-1.5 rounded-lg border border-blue-200 text-brand-cyan-text hover:bg-blue-50 disabled:opacity-50 transition-colors">
                    {copiedId === node.id ? 'Kopirano ✓' : 'Kopiraj curl komandu'}
                  </button>
                  <button
                    onClick={() => { setEditId(node.id); setEditDraft(modbusDraftFromNode(node)); setEditErr('') }}
                    className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors">
                    Uredi
                  </button>
                  <button
                    onClick={() => setConfirmRegen(node.id)}
                    className="text-xs px-2.5 py-1.5 rounded-lg border border-amber-200 text-amber-700 hover:bg-amber-50 transition-colors">
                    Regeneriši ključ
                  </button>
                  <button
                    onClick={() => toggleMut.mutate({ id: node.id, active: !node.active })}
                    className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors">
                    {node.active ? 'Deaktiviraj' : 'Aktiviraj'}
                  </button>
                  <button onClick={() => setConfirmDelete(node.id)}
                    className="text-xs px-2.5 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors">
                    Obriši
                  </button>
                </div>
              </div>

              {editId === node.id && (
                <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm">
                  <ModbusModeFields draft={editDraft} onChange={setEditDraft} />
                  {editErr && <p className="text-xs text-red-500 mt-2">{editErr}</p>}
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => updateModeMut.mutate({ id: node.id, body: modbusBody(editDraft) })}
                      disabled={updateModeMut.isPending || (editDraft.modbus_mode === 'master' && !editDraft.modbus_target_host.trim())}
                      className="px-3 py-1.5 text-xs font-medium bg-brand-cyan hover:bg-brand-cyan-dark disabled:opacity-50 text-brand-midnight rounded-lg transition-colors">
                      {updateModeMut.isPending ? 'Čuvanje…' : 'Sačuvaj'}
                    </button>
                    <button onClick={() => { setEditId(null); setEditErr('') }}
                      className="px-3 py-1.5 text-xs font-medium border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors">
                      Otkazati
                    </button>
                  </div>
                </div>
              )}

              {confirmRegen === node.id && (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                  <p className="text-amber-800 font-medium mb-2">
                    Regenerisanje ključa će onemogućiti trenutno edge čvor dok se novi ključ ne postavi u config.yaml. Nastaviti?
                  </p>
                  <div className="flex gap-2">
                    <button onClick={() => regenMut.mutate(node.id)} disabled={regenMut.isPending}
                      className="px-3 py-1.5 text-xs font-medium bg-amber-600 hover:bg-amber-700 text-white rounded-lg disabled:opacity-50 transition-colors">
                      {regenMut.isPending ? 'Regenerisanje…' : 'Potvrdi'}
                    </button>
                    <button onClick={() => setConfirmRegen(null)}
                      className="px-3 py-1.5 text-xs font-medium border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors">
                      Otkazati
                    </button>
                  </div>
                </div>
              )}

              {confirmDelete === node.id && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm">
                  <p className="text-red-800 font-medium mb-2">Obrisati edge čvor "{node.name}"? Ova akcija je nepovratna.</p>
                  <div className="flex gap-2">
                    <button onClick={() => deleteMut.mutate(node.id)} disabled={deleteMut.isPending}
                      className="px-3 py-1.5 text-xs font-medium bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-50 transition-colors">
                      {deleteMut.isPending ? 'Brisanje…' : 'Obriši'}
                    </button>
                    <button onClick={() => setConfirmDelete(null)}
                      className="px-3 py-1.5 text-xs font-medium border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors">
                      Otkazati
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Section>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function SettingsPage() {
  return (
    <div className="p-6 flex flex-wrap gap-6 items-start">
      <ImportSection />
      <EdgeNodesSection />
    </div>
  )
}
